import * as React from 'react';
import { SurveyVersionDataService, CharacteristicGroupDataService, CharacteristicDataService, ScoreTypeDataService, CharacteristicScoreDataService, StyleDataService, SurveyReportTypeDataService, SecondaryReportDataService, Auth } from '../../services';
import { SurveyVersion, TeamologyClarity, CharacteristicGroup, Characteristic, ScoreType, CharacteristicScore, Style, SurveyReportType, GridOption, SecondaryReport, TeamGrid } from '../../models';
import { NavLink } from 'react-router-dom';
import { LoadingIndicator, PVACharacteristicSelect, PVAPage } from '..';
import { BaseRouteProps } from '../../interfaces';
import { UserStore, FilterStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import './SurveyVersion.css';

interface SurveyVersionViewState {
    entity: SurveyVersion | undefined;
    scoreTypes: ScoreType[];
    characteristicGroups: CharacteristicGroup[];
    ungroupedCharacteristics?: any;
    characteristics: Characteristic[];
    characteristicScores: CharacteristicScore[];
    secondaryReports: SecondaryReport[];
    styles: Style[];
    loading: boolean;
    open: boolean;
    surveyReportTypes: SurveyReportType[];
    averageVal: string;
    stdDevVal: string;
}

interface SurveyVersionViewProps extends BaseRouteProps<{ id: number }> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    Auth?: Auth;
}

@inject('UserStore', 'FilterStore', 'Auth') @observer
export class SurveyVersionView extends React.Component<SurveyVersionViewProps, SurveyVersionViewState> {
    private surveyVersionDataService = new SurveyVersionDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private scoreTypeDataService = new ScoreTypeDataService(this.props.notificationService);
    private characteristicScoreDataService = new CharacteristicScoreDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private surveyReportTypeService = new SurveyReportTypeDataService(this.props.notificationService);
    private secondaryReportDataService = new SecondaryReportDataService(this.props.notificationService);

    @computed get isSuperAdmin() {
        return this.props.Auth!.userRoleIn([Roles.SuperAdmin]);
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    constructor(props: SurveyVersionViewProps) {
        super(props);

        this.state = {
            entity: undefined,
            scoreTypes: [],
            characteristicGroups: [],
            characteristics: [],
            characteristicScores: [],
            styles: [],
            loading: false,
            surveyReportTypes: [],
            secondaryReports: [],
            open: false,
            averageVal: '0',
            stdDevVal: '0',
        };
    }

    public componentDidMount() {
        this.surveyVersionDataService.get(this.props.match.params.id).then((surveyVersion: SurveyVersion) => {
            this.setState({
                entity: surveyVersion,
                averageVal: (surveyVersion.rFactor && surveyVersion.rFactor.average.toString()) || '0',
                stdDevVal: (surveyVersion.rFactor && surveyVersion.rFactor.stdDev.toString()) || '0',
            });

            this.surveyReportTypeService.getBySurvey(surveyVersion.surveyID).then((surveyReportTypes: SurveyReportType[]) => {
                this.setState({
                    surveyReportTypes: surveyReportTypes
                });
            });

            this.characteristicGroupDataService.getAllBySurveyVersion(surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
                this.setState({
                    characteristicGroups: characteristicGroups
                });
            });
            this.characteristicDataService.getAllBySurveyVersion(surveyVersion).then((characteristics: Characteristic[]) => {
                this.setState({
                    ungroupedCharacteristics: {name: 'Ungrouped', id: -1, surveyVersionID: surveyVersion.id, characteristics: characteristics.filter(x => !x.characteristicGroupID), characteristicGroupCharacteristics: []},
                    characteristics: characteristics
                });
            });
            this.scoreTypeDataService.getAllBySurveyVersion(surveyVersion).then((scoreTypes: ScoreType[]) => {
                this.setState({
                    scoreTypes: scoreTypes
                });
            });
            this.characteristicScoreDataService.getAllBySurveyVersion(surveyVersion).then((characteristicScores: CharacteristicScore[]) => {
                this.setState({
                    characteristicScores: characteristicScores
                });
            });
            this.styleDataService.getAllBySurveyVersionId(surveyVersion!.id).then((styles: Style[]) => {
                this.setState({
                    styles: styles
                });
            });
            this.secondaryReportDataService.getAllBySurveyVersion(surveyVersion).then((reports: SecondaryReport[]) => {
                this.setState({
                    secondaryReports: reports
                });
            });
        });
    }

    public publish() {
        if (!this.state.entity) {
            return;
        }
        if (confirm('Are you sure you want to publish this survey?')) {
            this.setState({
                loading: true
            });
            this.surveyVersionDataService.publish(this.state.entity).then(result => {
                if (this.state.entity !== undefined) {
                    this.props.history.push('/survey/' + this.state.entity.survey.id);
                } else {
                    this.setState({
                        loading: false,
                        open: false
                    });
                }
            });
        }
    }

    public publishBinder() {
        return this.publish.bind(this);
    }

    public clone() {
        if (!this.state.entity) {
            return;
        }
        if (confirm('Are you sure you want to clone this survey version? This process will take at least 30 seconds and it will create a brand new Survey.')) {
            this.setState({
                loading: true
            });
            this.surveyVersionDataService.clone(this.state.entity).then(result => {
                if (this.state.entity !== undefined) {
                    this.props.history.push('/survey/');
                } else {
                    this.setState({
                        loading: false,
                        open: false
                    });
                }
            });
        }
    }

    public cloneBinder() {
        return this.clone.bind(this);
    }

    public copy() {
        if (!this.state.entity) {
            return;
        }
        if (confirm('Are you sure you want to copy this survey version? This process will take at least 30 seconds.')) {
            this.setState({
                loading: true
            });
            this.surveyVersionDataService.copy(this.state.entity).then(result => {
                if (this.state.entity !== undefined) {
                    this.props.history.push('/surveyVersion/' + result);
                } else {
                    this.setState({
                        loading: false,
                        open: false
                    });
                }
            });
        }
    }

    public copyBinder() {
        return this.copy.bind(this);
    }

    public unlock() {
        if (!this.state.entity) {
            return;
        }
        if (confirm('Are you sure you want to unlock this survey version? This will clear any cached results to ensure scores are correct.')) {
            this.setState({
                loading: true
            });
            this.surveyVersionDataService.unlock(this.state.entity).then(result => {
                if (this.state.entity) {
                    var e = this.state.entity;
                    e.published = undefined;
                    e.active = false;
                    this.setState({
                        entity: e,
                        loading: false,
                        open: false
                    });
                } else {
                    this.setState({
                        loading: false,
                        open: false
                    });
                }
            });
        }
    }

    public unlockClear() {
        if (!this.state.entity) {
            return;
        }
        if (confirm('Are you sure you want to unlock this survey version? This will clear any cached results to ensure scores are correct.')) {
            this.setState({
                loading: true
            });
            this.surveyVersionDataService.unlockClear(this.state.entity).then(result => {
                if (this.state.entity) {
                    var e = this.state.entity;
                    e.published = undefined;
                    e.active = false;
                    this.setState({
                        entity: e,
                        loading: false,
                        open: false
                    });
                } else {
                    this.setState({
                        loading: false,
                        open: false
                    });
                }
            });
        }
    }

    public unlockBinder() {
        return this.unlock.bind(this);
    }

    public unlockClearBinder() {
        return this.unlockClear.bind(this);
    }

    public setActive() {
        if (!this.state.entity) {
            return;
        }
        this.setState({
            loading: true
        });
        this.surveyVersionDataService.setActive(this.state.entity).then(result => {
            if (this.state.entity) {
                var e = this.state.entity;
                e.active = true;
                this.setState({
                    entity: e,
                    loading: false,
                    open: false
                });
            }
        });
    }

    public setActiveBinder() {
        return this.setActive.bind(this);
    }

    private ActionsButton() {
        if (!this.state.entity) {
            return <div />;
        }

        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Actions</li>

                    {!this.state.entity.published && <li className="options-entry" onClick={this.publishBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" style={{color: 'red'}} /> <span style={{color: 'red'}}> Publish</span>
                        </div>
                        <div className="options-entry-desc">
                            Publish this survey version.
                        </div>
                    </li>}

                    {this.state.entity.published && this.state.entity.id !== 1 && <li className="options-entry" onClick={this.unlockBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" style={{color: 'red'}} /> <span style={{color: 'red'}}> Unlock</span>
                        </div>
                        <div className="options-entry-desc">
                            Unlock this Survey Version so that scoring changes can be made.
                        </div>
                    </li>}

                    {this.state.entity.published && this.state.entity.id !== 1 && <li className="options-entry" onClick={this.unlockClearBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" style={{color: 'red'}} /> <span style={{color: 'red'}}> Unlock & Clear Scores</span>
                        </div>
                        <div className="options-entry-desc">
                            Unlock this Survey Version so that scoring changes can be made and clear out scores.
                        </div>
                    </li>}

                    {this.state.entity.published && !this.state.entity.active && <li className="options-entry" onClick={this.setActiveBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Set Active</span>
                        </div>
                        <div className="options-entry-desc">
                            Mark this survey version as the default active survey version.
                        </div>
                    </li>}

                    <li className="options-entry" onClick={this.copyBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Copy</span>
                        </div>
                        <div className="options-entry-desc">
                            Copy this survey version into this survey as a new version.
                        </div>
                    </li>

                    <li className="options-header">New Survey</li>
                    <li className="options-entry" onClick={this.cloneBinder()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Copy to New Survey</span>
                        </div>
                        <div className="options-entry-desc">
                            Copy this survey version into a brand new survey
                        </div>
                    </li>
                </ul>
        </div>);
    }

    public render() {
        if (!this.state.entity) {
            return <div />;
        }

        let { characteristicGroups, ungroupedCharacteristics } = this.state;
        
        // I'm not sure how more than one group of ungrouped characteristics is being inserted as there is no setState happening
        // but for some reason we're getting the unsorted group added multiple times even though there is no looping to the add or anything
        // so we're just gonna make sure we haven't added the unsorted group
        if (characteristicGroups && characteristicGroups.length > 0) {
            characteristicGroups = characteristicGroups.filter( x => x.id > 0);
        } else {
            characteristicGroups = [];
        }
        
        if (ungroupedCharacteristics) {
            let maxSort = 0;

            if (characteristicGroups.length > 0) {
                characteristicGroups.forEach((group: CharacteristicGroup) => {
                    if (group.sort > maxSort) {
                        maxSort = group.sort;
                    }
                });
            }

            if (this.surveyReportType) {
                ungroupedCharacteristics.surveyReportTypeID = this.surveyReportType.id;
            }

            ungroupedCharacteristics.sort = maxSort + 1;

            characteristicGroups.push(ungroupedCharacteristics);
        }

        return (
            <div>
                <div className="content-header">
                    <div className="content-container">
                        <div className="row">
                            <div className="col-xs-7">
                                <div className="title">
                                    <NavLink to={'/survey/' + this.state.entity.surveyID}>
                                        <span className="glyphicon glyphicon-triangle-left" />
                                    </NavLink>
                                    &nbsp; 
                                    Survey Version #{this.state.entity.version} <span className="light small"> on '{this.state.entity.survey.name}' survey</span>
                                </div>
                                <i>Changes how a survey's statement responses calculate, generally there will be minor changes between versions.</i>
                            </div>
                            <div className="col-xs-5 text-right">
                                {this.ActionsButton()}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-body">
                    <div className="content-container">
                        {this.state.loading && <LoadingIndicator />}
                        {!this.state.loading && <div>
                            <div className="form-group">
                                <label>Nickname</label>
                                <input type="text" className="form-control" value={this.state.entity.nickname} onChange={(e) => { if (this.state.entity) { this.state.entity.nickname = e.target.value; } this.setState({entity: this.state.entity}); }} />
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-sm-6">
                                    <NavLink className="btn btn-default btn-block btn-large-block" to={'/surveyVersion/' + this.props.match.params.id + '/CharacteristicGroups'}>
                                        <span>Characteristic Groups</span>
                                    </NavLink>
                                </div>
                                <div className="col-sm-6">
                                    <NavLink className="btn btn-default btn-block btn-large-block" to={'/surveyVersion/' + this.props.match.params.id + '/Characteristics'}>
                                        <span>Characteristics</span>
                                    </NavLink>
                                </div>
                                {this.isSuperAdmin && <div className="col-sm-6">
                                    <NavLink className="btn btn-default btn-block btn-large-block" to={'/surveyVersion/' + this.props.match.params.id + '/scoretypes'}>
                                        <span>Raw Scores</span>
                                    </NavLink>
                                </div>}
                                <div className="col-sm-6">
                                    <NavLink className="btn btn-default btn-block btn-large-block" to={'/surveyVersion/' + this.props.match.params.id + '/styles'}>
                                        <span>The 8 PVA Styles</span>
                                    </NavLink>
                                </div>
                                <div className="col-sm-6">
                                    <NavLink className="btn btn-default btn-block btn-large-block" to={'/surveyVersion/' + this.props.match.params.id + '/secondaryreports'}>
                                        <span>Secondary Reports</span>
                                    </NavLink>
                                </div>
                            </div>

                            <h3>Team Grid Scales</h3>
                            <i>These are the default scales available on the Team Grid</i>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <td>Name</td>
                                        <td>Left Title</td>
                                        <td>Left Characteristic</td>
                                        <td>Right Title</td>
                                        <td>Right Characteristic</td>
                                        <td />
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        this.state.entity && this.state.entity.gridOptions.map((gridOpt: GridOption, ind: number) => {
                                            return <tr key={`cc-${ind}`}>
                                                <td>
                                                    <input type="text" value={gridOpt.name} onChange={(e) => this.setGridOptionName(ind, e.target.value)} className="form-control" />
                                                </td>
                                                <td>
                                                    <input type="text" value={gridOpt.characteristicOneLabel} onChange={(e) => this.setGridOptionLabelOne(ind, e.target.value)} className="form-control" />
                                                </td>
                                                <td>
                                                    <PVACharacteristicSelect showAll={true} options={this.state.characteristics} groups={this.state.characteristicGroups} selected={gridOpt.characteristicOne.id} onChange={(e: any) => { gridOpt.characteristicOne.id = parseInt(e.target.value, 10); this.forceUpdate(); }} />
                                                    {/* <select className="form-control" value={gridOpt.characteristicOne.id} onChange={(e) => { gridOpt.characteristicOne.id = parseInt(e.target.value, 10); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`c-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select> */}
                                                </td>
                                                <td>
                                                    <input type="text" value={gridOpt.characteristicTwoLabel} onChange={(e) => this.setGridOptionLabelTwo(ind, e.target.value)} className="form-control" />
                                                </td>
                                                <td>
                                                    <PVACharacteristicSelect showAll={true} options={this.state.characteristics} groups={this.state.characteristicGroups} selected={gridOpt.characteristicTwo.id} onChange={(e: any) => { gridOpt.characteristicTwo.id = parseInt(e.target.value, 10); this.forceUpdate(); }} />
                                                    {/* <select className="form-control" value={gridOpt.characteristicTwo.id} onChange={(e) => { gridOpt.characteristicTwo.id = parseInt(e.target.value, 10); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`c-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select> */}
                                                </td>
                                                <td>
                                                    <a className="btn btn-danger" onClick={(e) => this.removeGridOption(ind)}>Remove</a>
                                                </td>
                                            </tr>;
                                        })
                                    }
                                </tbody>
                            </table>
                            <a className="btn btn-default" onClick={(e) => { this.addGridOption(); }}>Add</a>

                            <h3>Teamology Clarity</h3>
                            <div>
                                <i>Determines the clarity of the scores. Formula: SUM ( ABSOLUTE ( Users Raw Score A - Users Raw Score B ) ) / COUNT</i>
                            </div>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <td>Raw Score A</td>
                                        <td>Raw Score B</td>
                                        <td />
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        this.state.entity.teamologyClarities.map((clarity: TeamologyClarity, clarityInd: number) => {
                                            return <tr key={`clarity-${clarityInd}`}>
                                                <td>
                                                    <select className="form-control" value={clarity.rawScoreOneID} onChange={(e) => { clarity.rawScoreOneID = parseInt(e.target.value, 10); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.scoreTypes.map((st: ScoreType) => {
                                                            return <option key={`clarity-${clarityInd}-st-${st.id}`} value={st.id}>{st.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="form-control" value={clarity.rawScoreTwoID} onChange={(e) => { clarity.rawScoreTwoID = parseInt(e.target.value, 10); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.scoreTypes.map((st: ScoreType) => {
                                                            return <option key={`clarity-${clarityInd}-st-${st.id}`} value={st.id}>{st.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                <td>
                                                    <a className="btn btn-danger" onClick={(e) => this.removeClarity(clarityInd)}>Remove</a>
                                                </td>
                                            </tr>;
                                        })
                                    }
                                </tbody>
                            </table>
                            <a className="btn btn-default" onClick={(e) => { this.addClarity(); }}>Add</a>

                            <h3>Team Grids</h3>
                            <div>
                                <i>Populates quadrants in the Team Grid Report</i>
                            </div>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <td>Name</td>
                                        <td>+Y Axis (Up)</td>
                                        <td>-Y Axis (Down)</td>
                                        <td>-X Axis (Left)</td>
                                        <td>+X Axis (Right)</td>
                                        {/* <td>Accommadate</td> */}
                                        <td />
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        this.state.entity.teamGrids.map((tg: TeamGrid, tgInd: number) => {
                                            return <tr key={`tg-${tgInd}`}>
                                                <td>
                                                    <input type="text" className="form-control" value={tg.name} onChange={(e) => { tg.name = e.target.value; this.forceUpdate(); }} />
                                                </td>
                                                <td>
                                                    <select className="form-control" value={tg.quadrantOneID} onChange={(e) => { tg.quadrantOneID = parseInt(e.target.value, 10); this.forceUpdate(); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`tg-one-${tgInd}-st-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="form-control" value={tg.quadrantTwoID} onChange={(e) => { tg.quadrantTwoID = parseInt(e.target.value, 10); this.forceUpdate(); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`tg-one-${tgInd}-st-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="form-control" value={tg.quadrantThreeID} onChange={(e) => { tg.quadrantThreeID = parseInt(e.target.value, 10); this.forceUpdate(); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`tg-one-${tgInd}-st-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="form-control" value={tg.quadrantFourID} onChange={(e) => { tg.quadrantFourID = parseInt(e.target.value, 10); this.forceUpdate(); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`tg-one-${tgInd}-st-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select>
                                                </td>
                                                {/* <td>
                                                    <select className="form-control" value={tg.quadrantAccomadateID} onChange={(e) => { tg.quadrantAccomadateID = parseInt(e.target.value, 10); }}>
                                                        <option value={0}>- Select -</option>
                                                        {this.state.characteristics.map((c: Characteristic) => {
                                                            return <option key={`tg-one-${tgInd}-st-${c.id}`} value={c.id}>{c.name}</option>;
                                                        })}
                                                    </select>
                                                </td> */}
                                                <td>
                                                    <a className="btn btn-danger" onClick={(e) => this.removeTeamGrid(tgInd)}>Remove</a>
                                                </td>
                                            </tr>;
                                        })
                                    }
                                </tbody>
                            </table>
                            <a className="btn btn-default" onClick={(e) => { this.addTeamGrid(); }}>Add</a>

                            <div>
                                <h3>R Factor Report Settings</h3>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div>Average</div>
                                        <input className="form-control" type="text" value={this.state.averageVal} onChange={this.setRFactorAverage} />
                                    </div>
                                    <div className="col-md-6">
                                        <div>Std Dev</div>
                                        <input className="form-control" type="text" value={this.state.stdDevVal} onChange={this.setRFactorStdDev} />
                                    </div>
                                </div>
                            </div>

                            <hr />
                            <a className="btn btn-primary" onClick={(e) => { this.submit(); }}>Save</a>
                        </div>}

                    </div>
                </div>
            </div>);
    }

    setRFactorAverage = (e: any) => {
        const { entity } = this.state;
        if (!entity) {
            return;
        }
        if (!entity.rFactor) {
            entity.rFactor = {
                id: 0,
                average: 0,
                stdDev: 0
            };
        }
        try {
            entity.rFactor.average = parseFloat(e.target.value);
        } catch {
            console.log('Failed to parse');
        }
        this.setState({
            entity,
            averageVal: e.target.value
        });
    }

    setRFactorStdDev = (e: any) => {
        const { entity } = this.state;
        if (!entity) {
            return;
        }
        if (!entity.rFactor) {
            entity.rFactor = {
                id: 0,
                average: 0,
                stdDev: 0
            };
        }
        try {
            entity.rFactor.stdDev = parseFloat(e.target.value);
        } catch {
            console.log('Failed to parse');
        }
        this.setState({
            entity,
            stdDevVal: e.target.value
        });
    }

    private addClarity() {
        var ent = this.state.entity;
        if (ent) {
            var c = new TeamologyClarity();
            ent.teamologyClarities.push(c);
            this.setState({
                entity: ent
            });
        }
    }

    private removeClarity(ind: number) {
        if (confirm('Are you sure want to remove this clarity check?')) {
            var ent = this.state.entity;
            if (ent) {
                ent.teamologyClarities.splice(ind, 1);
                this.setState({
                    entity: ent
                });
            }
        }
    }

    private addTeamGrid() {
        var ent = this.state.entity;
        if (ent) {
            var c = new TeamGrid();
            ent.teamGrids.push(c);
            this.setState({
                entity: ent
            });
        }
    }

    private removeTeamGrid(ind: number) {
        if (confirm('Are you sure want to remove this team grid?')) {
            var ent = this.state.entity;
            if (ent) {
                ent.teamGrids.splice(ind, 1);
                this.setState({
                    entity: ent
                });
            }
        }
    }

    private setGridOptionName(ind: number, v: string) {
        if (this.state.entity) {
            this.state.entity.gridOptions[ind].name = v;
            this.setState({
                entity: this.state.entity
            });
        }
    }
    private setGridOptionLabelOne(ind: number, v: string) {
        if (this.state.entity) {
            this.state.entity.gridOptions[ind].characteristicOneLabel = v;
            this.setState({
                entity: this.state.entity
            });
        }
    }
    private setGridOptionLabelTwo(ind: number, v: string) {
        if (this.state.entity) {
            this.state.entity.gridOptions[ind].characteristicTwoLabel = v;
            this.setState({
                entity: this.state.entity
            });
        }
    }

    private removeGridOption(ind: number) {
        if (this.state.entity) {
            this.state.entity.gridOptions.splice(ind, 1);
            this.setState({
                entity: this.state.entity
            });
        }
    }

    private submit() {
        if (this.state.entity) {
            this.surveyVersionDataService.update(this.state.entity).then(() => {
                // this.props.notificationService.addNotification(`Saved successfully.`);
            });
        }
    }

    addGridOption() {
        var entity = this.state.entity;
        if (entity) {
            var gridOpt = new GridOption();
            gridOpt.name = '';
            gridOpt.characteristicOne = new Characteristic();
            gridOpt.characteristicTwo = new Characteristic();
            gridOpt.characteristicOneLabel = '';
            gridOpt.characteristicTwoLabel = '';
            entity.gridOptions.push(gridOpt);
            this.setState({
                entity: entity
            });
        }
    }

}



// WEBPACK FOOTER //
// ./src/components/surveyversion/SurveyVersionView.tsx